import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useGetCorpGlobalSearchList } from "../UseCase";
import { replaceParamFromRoute } from "helpers/replaceParamFromRoute";
import { useTableQueryParams } from "hooks/useTableQueryParams";

import { TGetCorpGlobalFilter } from "../Model/types";
import { TSort } from "components/Table/types";
import {
  DEFAULT_FILTER_GLOBAL_CORP,
  DEFAULT_SORT_CORP_GLOBAL,
  MAP_SORT,
} from "../Model/constants";
import { DEFAULT_PAGINATION } from "components/Table/constants";
import { corpSlugParam, routePaths } from "routes/constants";

export const useCorpGlobalSearchListController = () => {
  const navigate = useNavigate();

  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const { searchParams, onSetSearchParams } =
    useTableQueryParams<TGetCorpGlobalFilter>({
      sortMap: MAP_SORT,
      defaultData: {
        sort: DEFAULT_SORT_CORP_GLOBAL,
        filter: {
          ...DEFAULT_FILTER_GLOBAL_CORP,
        },
      },
    });

  const sort = useMemo(() => {
    const splitedSort = searchParams?.constructedSort?.split(" ");
    if (splitedSort.length < 2) return {};
    return {
      sortKey: splitedSort[0],
      sortType: splitedSort[1],
    };
  }, [searchParams?.constructedSort]);

  const { corpGlobalList, isLoadingGetCorporationGlobalList } =
    useGetCorpGlobalSearchList({
      payload: {
        ...sort,
        ...searchParams?.filter,
        ...searchParams.page,
        bu: searchParams?.filter?.bu?.split(","),
      },
      onSuccess: (res) => {
        setPagination((prev) => ({
          ...prev,
          currentPage: res.page,
          itemPerPage: res.pageSize,
          totalItem: res.total,
        }));
      },
    });

  const handleSort = useCallback(
    (newSort: TSort) => {
      onSetSearchParams({
        sort: newSort,
      });
    },
    [onSetSearchParams]
  );

  const handleFilter = useCallback(
    (filter: Partial<TGetCorpGlobalFilter>) => {
      onSetSearchParams({ ...filter });
    },
    [onSetSearchParams]
  );

  const handleChangePage = useCallback(
    (newPage: number) => {
      onSetSearchParams({
        page: newPage,
      });
    },
    [onSetSearchParams]
  );

  const handleChangePerPage = useCallback(
    (perPage: number) => {
      onSetSearchParams({
        pageSize: perPage,
      });
    },
    [onSetSearchParams]
  );

  const handleSearch = useCallback(
    (keyword: string) => {
      onSetSearchParams({
        keyword: keyword,
      });
    },
    [onSetSearchParams]
  );

  const handleClickRow = useCallback(
    (id: string) => {
      const acc = corpGlobalList.find((acc) => acc.idUnique === id);
      if (!acc) return;
      const route = routePaths.root.corp.root.pCorpSlug.root;
      navigate(
        replaceParamFromRoute({
          param: [corpSlugParam],
          value: [id],
          route,
        })
      );
    },
    [corpGlobalList, navigate]
  );

  return {
    pagination,
    searchParams,
    corpGlobalList,
    sort,
    isLoadingGetCorporationGlobalList,
    onSort: handleSort,
    onFilter: handleFilter,
    onChangePage: handleChangePage,
    onChangePerPage: handleChangePerPage,
    onSearch: handleSearch,
    onClickRow: handleClickRow,
  };
};
